// ProfileStatus.tsx — Account moderation-status banner rendered on the profile page.
// Free-speech model: the account's posts STAY FULLY VISIBLE — this is informational only.
// Mirrors PostStatus's structure but framed for an account rather than a post.

import { useState } from 'react';
import styled from 'styled-components';
import { Icon } from '@/icons';
import { MetaRow } from '@/ui';
import { AppealModal } from '@/components/AppealModal';
import { CATEGORY_LABELS } from '@/core/models';
import { useAccountStatus, useRequireAuth } from '@/hooks';
import type { CaseRecord } from '@/core/models';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ProfileStatusProps {
  accountId: string;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

const Root = styled.div<{ $tone: 'amber' | 'danger' | 'success' }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: ${({ $tone, theme }) => {
    if ($tone === 'amber') return `${theme.color.accentAmber}1f`;
    if ($tone === 'danger') return `${theme.color.danger}1f`;
    return `${theme.color.success}1f`;
  }};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TitleIcon = styled(Icon)<{ $tone: 'amber' | 'danger' | 'success' }>`
  color: ${({ $tone, theme }) => {
    if ($tone === 'amber') return theme.color.accentAmber;
    if ($tone === 'danger') return theme.color.danger;
    return theme.color.success;
  }};
  flex-shrink: 0;
`;

const Title = styled.p<{ $tone: 'amber' | 'danger' | 'success' }>`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ $tone, theme }) => {
    if ($tone === 'amber') return theme.color.accentAmber;
    if ($tone === 'danger') return theme.color.danger;
    return theme.color.success;
  }};
`;

const Body = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.5;
`;

const AppealNote = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
  line-height: 1.5;
`;

const AppealButton = styled.button`
  align-self: flex-start;
  margin-top: 2px;
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: ${({ theme }) => `${theme.color.accentAmber}1f`};
  color: ${({ theme }) => theme.color.accentAmber};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ theme }) => `${theme.color.accentAmber}33`};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// ─── Inner renderer (takes pre-resolved case + appeal fn) ────────────────────

interface ProfileStatusInnerProps {
  c: CaseRecord;
  onAppeal: (caseId: string) => void;
}

function ProfileStatusInner({ c, onAppeal }: ProfileStatusInnerProps) {
  const { status: caseStatus } = c;

  // ── Under review ────────────────────────────────────────────────────────────
  // 'awaiting-head' renders IDENTICALLY — see the note in PostStatus. The
  // account holder is told the same true thing either way: a team is examining
  // the report and their posts stay fully visible.
  if (caseStatus === 'under-review' || caseStatus === 'awaiting-head') {
    return (
      <Root className="profile-status" role="status" $tone="amber">
        <TitleRow>
          <TitleIcon name="hourglass" size={16} $tone="amber" />
          <Title $tone="amber">
            <MetaRow>
              <span>Account Under Review</span>
              <span>This account is under review. Its posts stay fully visible.</span>
            </MetaRow>
          </Title>
        </TitleRow>
        <Body>
          A randomly-assembled Council team is examining the report.
        </Body>
      </Root>
    );
  }

  // ── Appeal under review ──────────────────────────────────────────────────────
  if (caseStatus === 'appeal-review') {
    return (
      <Root className="profile-status" role="status" $tone="amber">
        <TitleRow>
          <TitleIcon name="hourglass" size={16} $tone="amber" />
          <Title $tone="amber">
            <MetaRow>
              <span>Appeal under review</span>
              <span>The Council is re-examining this decision.</span>
            </MetaRow>
          </Title>
        </TitleRow>
      </Root>
    );
  }

  // ── Decided or appeal-resolved ────────────────────────────────────────────
  // Both statuses share a decided outcome view; only 'decided' gets an Appeal button.
  const isActioned = c.decision?.outcome === 'removed';
  const tone = isActioned ? 'danger' : 'success';
  const iconName = isActioned ? 'ban' : 'check';
  const title = isActioned
    ? `Account Actioned: ${CATEGORY_LABELS[c.category]}`
    : 'Account Reviewed: no action taken';

  return (
    <Root className="profile-status" role="status" $tone={tone}>
      <TitleRow>
        <TitleIcon name={iconName} size={16} $tone={tone} />
        <Title $tone={tone}>{title}</Title>
      </TitleRow>

      {c.decision?.explanation && (
        <Body>{c.decision.explanation}</Body>
      )}

      {c.appeal && (
        <AppealNote>
          Appeal {c.appeal.outcome}. {c.appeal.explanation}
        </AppealNote>
      )}

      {caseStatus === 'decided' && (
        <AppealButton type="button" onClick={() => onAppeal(c.id)}>
          Appeal
        </AppealButton>
      )}
    </Root>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

export function ProfileStatus({ accountId }: ProfileStatusProps) {
  const c = useAccountStatus(accountId);
  const requireAuth = useRequireAuth();
  const [appealingCaseId, setAppealingCaseId] = useState<string | null>(null);

  if (!c) return null;

  const handleAppeal = (caseId: string) => {
    if (!requireAuth('appeal')) return;
    // The server requires a written reason — collect it before POSTing.
    setAppealingCaseId(caseId);
  };

  return (
    <>
    <ProfileStatusInner
      c={c}
      onAppeal={handleAppeal}
    />
    {/* Appeal modal — collects the required reason */}
    {appealingCaseId && (
      <AppealModal caseId={appealingCaseId} onClose={() => setAppealingCaseId(null)} />
    )}
    </>
  );
}
